export const NoteFlowType = {
	REESE: "reese",
	MODULES: "modules",
} as const;

export const GeneralRoomOrder = {
	START: "start",
	END: "end",
} as const;

export type NoteFlowType = "reese" | "modules";
export type GeneralRoomOrder = "start" | "end";

export type EnabledNoteFlowTypes = {
	reese: boolean;
	modules: boolean;
};

export const DEFAULT_ENABLED_NOTE_FLOW_TYPES: EnabledNoteFlowTypes = {
	reese: true,
	modules: true,
};

type OrganizationCustomSettings = Record<string, unknown>;

export const estimate_types = [
	"MITIGATION",
	"RECONSTRUCTION",
	"CONTENTS",
	"MOLD",
	"FIRE_MITIGATION",
	"BIOHAZARD",
	"TIME_AND_MATERIAL",
] as const;

export type ProfitOverheadEstimateType = (typeof estimate_types)[number];

export type ProfitAndOverheadRow = {
	profit?: number;
	overhead?: number;
};

export type ProfitOverheadFormValues = Partial<Record<ProfitOverheadEstimateType, ProfitAndOverheadRow>>;
/** Whole-number percent when a cell is blank or invalid (must match PDF/backend default). */
const DEFAULT_PERCENT = 10;

/**
 * Profit/overhead cell: finite numbers truncate to integers. Otherwise coerce via string
 * (DB/API may send strings, spaces, or odd types) then parse; blank or non-numeric → default.
 */
const normalizeInputValue = (value: unknown): number => {
	if (typeof value === "number" && Number.isFinite(value)) {
		return Math.trunc(value);
	}
	const raw = value == null ? "" : typeof value === "string" ? value : "";
	const trimmed = raw.trim();
	if (trimmed === "") return DEFAULT_PERCENT;
	const parsed = Number(trimmed);
	if (!Number.isFinite(parsed)) return DEFAULT_PERCENT;
	return Math.trunc(parsed);
};

export interface OrganizationCustomSettingsFormValues {
	status: { name: string; color: string }[];
	offices: string[];
	XNAddresses: string[];
	tpas: string[];
	carriers: string[];
	extraFields: string[];
	requiredFields: string[];
	enabledNoteFlowTypes: EnabledNoteFlowTypes;
	generalRoomOrder?: GeneralRoomOrder;
	customProfitAndOverhead?: ProfitOverheadFormValues;
}

export const parseOrganizationCustomSettings = (customSettings: unknown): OrganizationCustomSettings => {
	if (!customSettings) {
		return {};
	}

	let parsedSettings: unknown = customSettings;
	try {
		// The admin API may return customSettings as an object or as one JSON string.
		parsedSettings = typeof parsedSettings === "string" ? JSON.parse(parsedSettings) : parsedSettings;
	} catch (err) {
		return {};
	}

	if (!parsedSettings || typeof parsedSettings !== "object" || Array.isArray(parsedSettings)) {
		return {};
	}

	return parsedSettings as OrganizationCustomSettings;
};

export const getEnabledNoteFlowTypesFromSettings = (customSettings: unknown): EnabledNoteFlowTypes => {
	const raw = (parseOrganizationCustomSettings(customSettings) as { enabledNoteFlowTypes?: unknown }).enabledNoteFlowTypes;
	if (!raw || typeof raw !== "object") {
		return { ...DEFAULT_ENABLED_NOTE_FLOW_TYPES };
	}
	const record = raw as Record<string, unknown>;
	return {
		reese: typeof record.reese === "boolean" ? record.reese : DEFAULT_ENABLED_NOTE_FLOW_TYPES.reese,
		modules: typeof record.modules === "boolean" ? record.modules : DEFAULT_ENABLED_NOTE_FLOW_TYPES.modules,
	};
};

export const getGeneralRoomOrderFromSettings = (customSettings: unknown): GeneralRoomOrder => {
	const generalRoomOrder = parseOrganizationCustomSettings(customSettings).generalRoomOrder;
	if (generalRoomOrder === GeneralRoomOrder.START || generalRoomOrder === GeneralRoomOrder.END) {
		return generalRoomOrder;
	}

	return GeneralRoomOrder.START;
};

export const getProfitOverheadMap = (customSettings: unknown): ProfitOverheadFormValues => {
	const typedSettings = parseOrganizationCustomSettings(customSettings) as {
		customProfitAndOverhead?: unknown;
	};
	const raw = typedSettings.customProfitAndOverhead;
	if (!raw || typeof raw !== "object") {
		return {};
	}
	const byEstimateType: ProfitOverheadFormValues = {};
	for (const estimateType of estimate_types) {
		const entry = (raw as Record<string, unknown>)[estimateType];
		if (!entry || typeof entry !== "object") {
			continue;
		}
		const profit = normalizeInputValue((entry as Record<string, unknown>).profit);
		const overhead = normalizeInputValue((entry as Record<string, unknown>).overhead);
		byEstimateType[estimateType] = { profit, overhead };
	}
	return byEstimateType;
};

export const buildOrganizationCustomSettings = (existingCustomSettings: unknown, values: OrganizationCustomSettingsFormValues): string => {
	const nextSettings: OrganizationCustomSettings = {
		...parseOrganizationCustomSettings(existingCustomSettings),
		status: values.status,
		offices: values.offices,
		XNAddresses: values.XNAddresses,
		tpas: values.tpas,
		carriers: values.carriers,
		extraFields: values.extraFields,
		requiredFields: values.requiredFields,
	};

	// Legacy fields replaced by the simple enabledNoteFlowTypes toggle; drop them from the persisted JSON.
	delete nextSettings.noteFlowType;
	delete nextSettings.noteFlowTypeByEstimateType;
	nextSettings.enabledNoteFlowTypes = {
		reese: values.enabledNoteFlowTypes.reese === true,
		modules: values.enabledNoteFlowTypes.modules === true,
	};
	nextSettings.generalRoomOrder =
		values.generalRoomOrder === GeneralRoomOrder.END ? GeneralRoomOrder.END : GeneralRoomOrder.START;
	// Always persist both fields per estimate type so API merge cannot keep stale nested keys when a cell is cleared.
	const byEstimateType = {} as Record<ProfitOverheadEstimateType, { profit: number; overhead: number }>;
	for (const estimateType of estimate_types) {
		const row = values.customProfitAndOverhead?.[estimateType];
		const profit = normalizeInputValue(row?.profit);
		const overhead = normalizeInputValue(row?.overhead);
		byEstimateType[estimateType] = { profit, overhead };
	}
	nextSettings.customProfitAndOverhead = byEstimateType;

	return JSON.stringify(nextSettings);
};
